import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { StripePaymentMethodData } from 'src/app/core/services/subscription-plan.service';
import { UserContextService } from 'src/app/core/services/user-context.service';
import { SharedService } from 'src/app/core/services/shared-service';
import { SubscriptionEntityExtended } from 'mj_generatedentities';
import { 
  loadStripe, 
  Stripe, 
  StripePaymentElement,
  StripeElements,
  SetupIntent
} from '@stripe/stripe-js';
import { environment } from 'src/environments/environment';
import { STRIPE_CARD_BRAND_ICONS } from 'src/app/core/util';
import { SubscriptionPlanService } from 'src/app/core/services/subscription-plan.service';

interface PaymentMethodDisplay extends StripePaymentMethodData {
  cardIconUrl: string;
  expiryDisplay?: string;
}

@Component({
  selector: 'app-subscription-edit-payment',
  templateUrl: './subscription-edit-payment.component.html',
  styleUrls: ['./subscription-edit-payment.component.css']
})
export class SubscriptionEditPaymentComponent implements OnInit, OnDestroy {
  readonly STRIPE_CARD_BRAND_ICONS = STRIPE_CARD_BRAND_ICONS;

  subscriptionID!: string;
  subscription: SubscriptionEntityExtended | null = null;
  paymentMethods: PaymentMethodDisplay[] = [];
  selectedPaymentMethodId: string | null = null;
  currentPaymentMethodId: string | null = null;
  isAddingNewCard = false;
  isLoading = true;
  isSubmitting = false;
  loadingMessage = 'Loading payment methods...';
  isStripeLoading = false;

  private stripe: Stripe | null = null;
  private elements: StripeElements | null = null;
  private paymentElement: StripePaymentElement | null = null;
  private retryCount = 0;
  private maxRetries = 5;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sharedService: SharedService,
    private userContextService: UserContextService,
    private subscriptionService: SubscriptionPlanService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit(): Promise<void> {
    this.subscriptionID = this.route.snapshot.paramMap.get('subscriptionId')!;
    
    // Check for redirect status
    const setupIntentResult = await this.handleRedirect();
    if (!setupIntentResult) {
      await this.loadData();
    }
  }

  ngOnDestroy(): void {
    if (this.paymentElement) {
      this.paymentElement.destroy();
    }
    this.elements = null;
    this.stripe = null;
  }

  private async loadData(): Promise<void> {
    try {
      this.isLoading = true;
      this.loadingMessage = 'Loading subscription details...';

      // Get subscription
      this.subscription = this.userContextService.Subscriptions?.find(sub => sub.ID === this.subscriptionID);
      if (!this.subscription) {
        this.sharedService.DisplayNotification('Subscription not found', 'error');
        this.router.navigate(['/settings/subscription-list']);
        return;
      }

      // Load payment methods
      const data = await this.subscriptionService.GetStripeSubscriptionData(this.subscription);
      if (data?.paymentMethod) {
        this.currentPaymentMethodId = data.paymentMethod.id;
        this.selectedPaymentMethodId = data.paymentMethod.id;
        this.paymentMethods = [this.mapToPaymentMethodDisplay(data.paymentMethod)];
      }

      this.isLoading = false;
    } catch (error) {
      console.error('Error loading data:', error);
      this.sharedService.DisplayNotification('Failed to load payment methods', 'error');
      this.isLoading = false;
    }
  }

  private mapToPaymentMethodDisplay(method: StripePaymentMethodData): PaymentMethodDisplay {
    return {
      ...method,
      cardIconUrl: this.getCardBrandIcon(method.card?.brand || 'unknown'),
      expiryDisplay: method.card ? `${method.card.exp_month}/${method.card.exp_year}` : undefined
    };
  }

  private getCardBrandIcon(brand: string): string {
    const normalizedBrand = brand.toLowerCase();
    return STRIPE_CARD_BRAND_ICONS[normalizedBrand] || STRIPE_CARD_BRAND_ICONS.unknown;
  }

  private async initializeStripe(): Promise<void> {
    if (!this.subscription) return;

    try {
      // Clean up any existing elements
      if (this.paymentElement) {
        this.paymentElement.destroy();
        this.paymentElement = null;
      }

      this.cdr.detectChanges();
      await new Promise(resolve => setTimeout(resolve, 250));

      const paymentElement = document.getElementById('payment-element');
      if (!paymentElement) {
        throw new Error('Payment element not found in DOM');
      }

      // Initialize Stripe if not already done
      if (!this.stripe) {
        this.stripe = await loadStripe(environment.STRIPE_PUBLISHABLE_KEY);
        if (!this.stripe) {
          throw new Error('Failed to load Stripe');
        }
      }

      // Get SetupIntent client secret
      const clientSecret = await this.subscriptionService.CreateSetupIntent(this.subscription);
      
      // Create Elements instance with appearance customization
      this.elements = this.stripe.elements({
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#0570de',
            colorBackground: '#ffffff',
            colorText: '#30313d',
            colorDanger: '#df1b41',
            fontFamily: 'Ideal Sans, system-ui, sans-serif',
            spacingUnit: '4px',
            borderRadius: '4px'
          }
        }
      });

      // Create and mount the Payment Element
      this.paymentElement = this.elements.create('payment', {
        layout: 'tabs',
        defaultValues: {
          billingDetails: {
            name: this.subscription.Organization || ''
          }
        }
      });

      // Mount the payment element
      this.paymentElement.mount('#payment-element');
      this.retryCount = 0; // Reset retry count on successful mount
    } catch (error) {
      console.error('Error initializing Stripe:', error);
      
      // Retry logic for mounting errors
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        console.log(`Retrying Stripe initialization (attempt ${this.retryCount}/${this.maxRetries})...`);
        await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms before retrying
        await this.initializeStripe();
        return;
      }

      this.sharedService.DisplayNotification('Failed to initialize payment form', 'error');
      this.isAddingNewCard = false;
    } finally {
      this.isStripeLoading = false;
    }
  }

  onPaymentMethodSelect(methodId: string): void {
    this.selectedPaymentMethodId = methodId;
    this.isAddingNewCard = false;
  }

  async onAddNewCard(): Promise<void> {
    this.selectedPaymentMethodId = null;
    this.isAddingNewCard = true;
    this.isStripeLoading = true;
    this.retryCount = 0; // Reset retry count
    await this.initializeStripe();
  }

  onBack(): void {
    this.router.navigate(['/settings/subscription', this.subscriptionID, 'details']);
  }

  private async handleRedirect(): Promise<boolean> {
    const params = new URLSearchParams(window.location.search);
    const setupIntentId = params.get('setup_intent');
    const redirectStatus = params.get('redirect_status');

    if (setupIntentId && redirectStatus) {
      this.isSubmitting = true;

      try {
        if (redirectStatus !== 'succeeded') {
          throw new Error('Failed to set up payment method');
        }

        // Initialize Stripe
        this.stripe = await loadStripe(environment.STRIPE_PUBLISHABLE_KEY);
        if (!this.stripe) {
          throw new Error('Failed to load Stripe');
        }

        // Retrieve the SetupIntent to get the payment method ID
        const { setupIntent } = await this.stripe.retrieveSetupIntent(params.get('setup_intent_client_secret'));
        
        if (!setupIntent?.payment_method) {
          throw new Error('No payment method returned from Stripe');
        }

        // Update subscription with the new payment method
        const updateResult = await this.subscriptionService.UpdateSubscriptionPaymentMethod(
          this.subscription,
          setupIntent.payment_method as string
        );

        if (!updateResult.success) {
          throw new Error(updateResult.error || 'Failed to update payment method');
        }

        this.sharedService.DisplayNotification('Payment method updated successfully', 'success');
        this.router.navigate(['/settings/subscription', this.subscriptionID, 'details']);
        return true;
      } catch (error) {
        console.error('Error handling redirect:', error);
        let errorMessage = 'Failed to update payment method';
        
        if (error.message) {
          errorMessage = error.message;
        } else if (error.type === 'validation_error') {
          errorMessage = 'Please check your card details and try again';
        } else if (error.type === 'card_error') {
          errorMessage = error.message || 'Your card was declined';
        }
        
        this.sharedService.DisplayNotification(errorMessage, 'error');
        return true;
      } finally {
        this.isSubmitting = false;
        // Clear the URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }

    return false;
  }

  async onSubmit(): Promise<void> {
    if (!this.subscription) return;

    this.isSubmitting = true;

    try {
      if (this.isAddingNewCard) {
        if (!this.stripe || !this.elements) {
          throw new Error('Stripe not initialized');
        }

        // Check if the payment element is mounted
        if (!this.paymentElement) {
          throw new Error('Payment element not initialized');
        }

        // Confirm the SetupIntent, redirecting only if required (e.g., for 3D Secure)
        const result = await this.stripe.confirmSetup({
          elements: this.elements,
          redirect: 'if_required',
          confirmParams: {
            return_url: window.location.href // Required for cases where redirect is needed
          }
        });

        if (result.error) {
          throw result.error;
        }

        // Type assertion for successful result with proper unknown conversion
        const setupIntent = (result as unknown as { setupIntent: SetupIntent }).setupIntent;
        if (!setupIntent?.payment_method) {
          throw new Error('No payment method returned from Stripe');
        }

        // Update subscription with the new payment method
        const updateResult = await this.subscriptionService.UpdateSubscriptionPaymentMethod(
          this.subscription,
          setupIntent.payment_method as string
        );

        if (!updateResult.success) {
          throw new Error(updateResult.error || 'Failed to update payment method');
        }

        this.sharedService.DisplayNotification('Payment method updated successfully', 'success');
        this.router.navigate(['/settings/subscription', this.subscriptionID, 'details']);
      } else if (this.selectedPaymentMethodId) {
        // Update subscription with existing payment method
        const updateResult = await this.subscriptionService.UpdateSubscriptionPaymentMethod(
          this.subscription,
          this.selectedPaymentMethodId
        );

        if (!updateResult.success) {
          throw new Error(updateResult.error || 'Failed to update payment method');
        }

        this.sharedService.DisplayNotification('Payment method updated successfully', 'success');
        this.router.navigate(['/settings/subscription', this.subscriptionID, 'details']);
      }
    } catch (error) {
      console.error('Error updating payment method:', error);
      let errorMessage = 'Failed to update payment method';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.type === 'validation_error') {
        errorMessage = 'Please check your card details and try again';
      } else if (error.type === 'card_error') {
        errorMessage = error.message || 'Your card was declined';
      }
      
      this.sharedService.DisplayNotification(errorMessage, 'error');
    } finally {
      this.isSubmitting = false;
    }
  }

  isUpdateAllowed(): boolean {
    if (this.isAddingNewCard) {
      return true;
    }
    return this.selectedPaymentMethodId !== null && this.selectedPaymentMethodId !== this.currentPaymentMethodId;
  }
} 